import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import api from "@/lib/api";
import {
  TrendingUp,
  TrendingDown,
  X,
  Heart,
  Info,
  RefreshCw,
  Flame,
  Eye,
  Radar,
  ChevronUp,
  ChevronDown,
  Target,
  Shield,
  Activity,
  Sparkles,
  Layers,
  Zap,
  CheckCircle2,
  Brain,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";

const TIER_META = {
  ACTION: { label: "STRONG",  color: "text-emerald-300", bg: "bg-emerald-500/20", border: "border-emerald-400/40", icon: Flame, sub: "3+ pillars · high conviction" },
  WATCH:  { label: "SOLID",   color: "text-amber-300",   bg: "bg-amber-500/20",   border: "border-amber-400/40",   icon: Eye,   sub: "2+ pillars · forming" },
  EARLY:  { label: "EARLY",   color: "text-sky-300",     bg: "bg-sky-500/20",     border: "border-sky-400/40",     icon: Radar, sub: "1 pillar · thin signal" },
};

const PILLAR_META = {
  fundamental:      { label: "FUND", icon: Layers,   color: "bg-emerald-500" },
  technical:        { label: "TECH", icon: Activity, color: "bg-sky-500" },
  unusual_activity: { label: "FLOW", icon: Zap,      color: "bg-amber-500" },
  catalyst:         { label: "CAT",  icon: Sparkles, color: "bg-fuchsia-500" },
};

const SOURCE_LABEL = {
  trump_tracker:       "🇺🇸 Trump",
  synthesis_agent:     "🧩 Synthesis",
  earnings_eve_agent:  "📅 Earnings",
  edgar_form4:         "🏛️ Form 4",
  openfda:             "💊 FDA",
  reddit_stocktwits:   "💬 Social",
  stocktwits:          "💬 StockTwits",
  news_catalyst:       "📰 News",
};

const DISMISSED_KEY = "sohel_dismissed_picks_v1";
const INTERESTED_KEY = "sohel_interested_picks_v1";

function readLocalSet(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
}
function writeLocalSet(key, set) {
  try { localStorage.setItem(key, JSON.stringify([...set])); } catch {}
}

function PillarDots({ pillars }) {
  return (
    <div className="flex items-center gap-1.5">
      {["fundamental", "technical", "unusual_activity", "catalyst"].map((d) => {
        const meta = PILLAR_META[d];
        const Icon = meta.icon;
        const filled = pillars?.[d]?.filled;
        return (
          <div
            key={d}
            className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
              filled ? `${meta.color} text-white` : "bg-zinc-800 text-zinc-600"
            }`}
            title={`${meta.label} ${filled ? `score ${pillars[d].score}` : "pending"}`}
          >
            <Icon className="w-2.5 h-2.5" />
            {meta.label}
          </div>
        );
      })}
    </div>
  );
}

function SwipeCard({ pick, onSwipe, onTap, top }) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-300, 300], [-15, 15]);
  const yesOpacity = useTransform(x, [0, 150], [0, 1]);
  const noOpacity = useTransform(x, [-150, 0], [1, 0]);

  const tierMeta = TIER_META[pick.tier] || TIER_META.RADAR;
  const TierIcon = tierMeta.icon;
  const isBuy = pick.direction === "BUY";
  const upsideTxt = pick.upside_pct != null ? `${pick.upside_pct > 0 ? "+" : ""}${pick.upside_pct.toFixed(2)}%` : "—";

  const handleDragEnd = (_, info) => {
    if (info.offset.x > 120) onSwipe("right");
    else if (info.offset.x < -120) onSwipe("left");
  };

  return (
    <motion.div
      data-testid={`pick-card-${pick.symbol}`}
      className={`absolute inset-0 ${top ? "z-10" : "z-0 scale-95 opacity-60 pointer-events-none"}`}
      style={top ? { x, rotate } : {}}
      drag={top ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={1}
      onDragEnd={top ? handleDragEnd : undefined}
      whileTap={top ? { cursor: "grabbing" } : undefined}
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: top ? 1 : 0.95, opacity: top ? 1 : 0.6 }}
      exit={{ x: x.get() > 0 ? 600 : -600, opacity: 0, rotate: x.get() > 0 ? 25 : -25 }}
      transition={{ duration: 0.25 }}
    >
      <Card className="card-dark border-border h-full overflow-hidden relative cursor-grab active:cursor-grabbing select-none">
        {/* YES/NO overlays */}
        {top && (
          <>
            <motion.div
              style={{ opacity: yesOpacity }}
              className="absolute top-4 right-4 z-20 px-4 py-2 rounded-lg border-4 border-emerald-400 text-emerald-300 text-xl font-bold rotate-12"
            >
              ★ INTERESTED
            </motion.div>
            <motion.div
              style={{ opacity: noOpacity }}
              className="absolute top-4 left-4 z-20 px-4 py-2 rounded-lg border-4 border-rose-400 text-rose-300 text-xl font-bold -rotate-12"
            >
              DISMISS
            </motion.div>
          </>
        )}

        <CardContent className="p-5 md:p-7 h-full flex flex-col gap-4">
          {/* Header — ticker + tier + direction */}
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl md:text-6xl font-data font-black text-white tracking-tight">{pick.symbol}</span>
                {pick.promise_active && (
                  <Badge className="bg-purple-500/20 text-purple-300 border-purple-400/40 border text-[10px]">In Promise</Badge>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={`${tierMeta.bg} ${tierMeta.color} ${tierMeta.border} border text-[11px] font-bold uppercase tracking-wider`}>
                  <TierIcon className="w-3 h-3 mr-1" /> {tierMeta.label}
                </Badge>
                <Badge className={`${isBuy ? "bg-emerald-500/20 text-emerald-300 border-emerald-400/40" : "bg-rose-500/20 text-rose-300 border-rose-400/40"} border text-[11px] font-bold uppercase`}>
                  {isBuy ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                  {pick.direction}
                </Badge>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Conviction</div>
              <div className={`text-4xl md:text-5xl font-data font-black ${
                pick.score >= 70 ? "text-emerald-300" : pick.score >= 55 ? "text-amber-300" : "text-sky-300"
              }`}>
                {pick.score}
              </div>
              <div className="text-[9px] text-muted-foreground">
                avg of {pick.filled_pillars} verified pillar{pick.filled_pillars !== 1 ? "s" : ""}
              </div>
            </div>
          </div>

          {/* Plain English thesis */}
          <div className="bg-black/40 border border-zinc-800 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Why this pick</div>
            <p className="text-sm text-zinc-100 leading-relaxed">{pick.thesis}</p>
          </div>

          {/* 🔬 Pattern Backtest — historical analogs for this fingerprint */}
          {pick.backtest && pick.backtest.hit_rate != null && (
            <div className={`rounded-lg p-3 border ${
              pick.backtest.hit_rate >= 60 ? "bg-emerald-500/10 border-emerald-400/30" :
              pick.backtest.hit_rate >= 50 ? "bg-amber-500/10 border-amber-400/30" :
                                              "bg-rose-500/10 border-rose-400/30"
            }`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-wider text-zinc-300">
                  🔬 Pattern Backtest
                  {pick.backtest.level && (
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 text-[9px] uppercase">
                      {pick.backtest.level === "tight" ? "4-signal" :
                       pick.backtest.level === "medium" ? "3-signal" : "1-signal"}
                    </span>
                  )}
                </span>
                <span className={`text-[10px] font-bold ${
                  pick.backtest.hit_rate >= 60 ? "text-emerald-300" :
                  pick.backtest.hit_rate >= 50 ? "text-amber-300" : "text-rose-300"
                }`}>
                  {pick.backtest.hit_rate >= 60 ? "EDGE+" :
                   pick.backtest.hit_rate >= 50 ? "NEUTRAL" : "EDGE−"}
                </span>
              </div>
              <p className="text-xs text-zinc-100 leading-snug">
                Last <b>{pick.backtest.sample_size}</b> times {pick.symbol} fired this pattern:{" "}
                <b>{pick.backtest.hit_rate}%</b> hit, avg{" "}
                <b className={pick.backtest.avg_move_pct >= 0 ? "text-emerald-300" : "text-rose-300"}>
                  {pick.backtest.avg_move_pct >= 0 ? "+" : ""}{pick.backtest.avg_move_pct}%
                </b>{" "}
                in {pick.backtest.horizon_days}d.
              </p>
              {pick.backtest.last10_hit_rate != null && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  Last 10 analogs: {pick.backtest.last10_hit_rate}% hit · avg{" "}
                  <span className={pick.backtest.last10_avg_pct >= 0 ? "text-emerald-300" : "text-rose-300"}>
                    {pick.backtest.last10_avg_pct >= 0 ? "+" : ""}{pick.backtest.last10_avg_pct}%
                  </span>
                </p>
              )}
              {pick.backtest.fingerprint && (
                <p className="text-[10px] text-zinc-500 mt-1 italic">{pick.backtest.fingerprint}</p>
              )}
            </div>
          )}

          {/* 🎯 Edge Rarity + 🥷 Consensus flag */}
          <div className="flex flex-wrap gap-2 text-[10px]">
            {pick.edge && (
              <div className="flex-1 min-w-0 bg-black/40 border border-zinc-800 rounded p-2">
                <div className="text-muted-foreground uppercase tracking-wider mb-0.5">🎯 Edge Rarity</div>
                <div className="text-zinc-100 leading-snug">{pick.edge.rarity_label}</div>
              </div>
            )}
            {pick.consensus?.label && (
              <div className={`flex-1 min-w-0 rounded p-2 border ${
                pick.consensus.trending_on_social
                  ? "bg-amber-500/10 border-amber-400/30"
                  : "bg-emerald-500/10 border-emerald-400/30"
              }`}>
                <div className="text-muted-foreground uppercase tracking-wider mb-0.5">
                  {pick.consensus.trending_on_social ? "⚠️ Crowd" : "🥷 Off-radar"}
                </div>
                <div className={`leading-snug ${
                  pick.consensus.trending_on_social ? "text-amber-200" : "text-emerald-200"
                }`}>
                  {pick.consensus.label}
                </div>
              </div>
            )}
          </div>

          {/* Prices grid */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-black/40 border border-zinc-800 rounded-lg p-2.5">
              <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-muted-foreground">
                <Target className="w-3 h-3" /> Entry
              </div>
              <div className="text-lg md:text-xl font-data font-bold text-white mt-0.5">
                ${pick.entry?.toFixed(2) ?? "—"}
              </div>
            </div>
            <div className="bg-black/40 border border-emerald-900/50 rounded-lg p-2.5">
              <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-emerald-300/70">
                <TrendingUp className="w-3 h-3" /> Target
              </div>
              <div className="text-lg md:text-xl font-data font-bold text-emerald-300 mt-0.5">
                ${pick.target?.toFixed(2) ?? "—"}
              </div>
              <div className="text-[10px] text-emerald-300/60">{upsideTxt}</div>
            </div>
            <div className="bg-black/40 border border-rose-900/50 rounded-lg p-2.5">
              <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-rose-300/70">
                <Shield className="w-3 h-3" /> Stop
              </div>
              <div className="text-lg md:text-xl font-data font-bold text-rose-300 mt-0.5">
                ${pick.stop?.toFixed(2) ?? "—"}
              </div>
              <div className="text-[10px] text-rose-300/60">R/R {pick.rr ?? "—"}</div>
            </div>
          </div>

          {/* Pillars + sources */}
          <div className="space-y-2">
            <PillarDots pillars={pick.pillars} />
            <div className="flex flex-wrap gap-1">
              {(pick.sources || []).slice(0, 4).map((s) => (
                <Badge key={s} className="bg-zinc-800/80 text-zinc-300 border-0 text-[10px]">
                  {SOURCE_LABEL[s] || s}
                </Badge>
              ))}
            </div>
          </div>

          <div className="flex-1" />

          {/* Hint */}
          {top && (
            <div className="text-center text-[10px] text-zinc-600 uppercase tracking-wider">
              ← swipe to dismiss · swipe to like →
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

function DetailDialog({ pick, onClose }) {
  if (!pick) return null;
  return (
    <Dialog open={!!pick} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="card-dark border-border max-w-lg">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-3xl font-data font-black text-white">{pick.symbol}</span>
            <Badge className="border bg-emerald-500/20 text-emerald-300 border-emerald-400/40">{pick.direction}</Badge>
          </div>
          <p className="text-sm text-zinc-100">{pick.thesis}</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-black/40 border border-zinc-800 rounded p-2">
              <div className="text-muted-foreground">Score</div>
              <div className="text-lg font-data font-bold text-white">{pick.score}/100</div>
            </div>
            <div className="bg-black/40 border border-zinc-800 rounded p-2">
              <div className="text-muted-foreground">Pillars</div>
              <div className="text-lg font-data font-bold text-white">{pick.filled_pillars}/4</div>
            </div>
            <div className="bg-black/40 border border-zinc-800 rounded p-2">
              <div className="text-muted-foreground">Entry → Target</div>
              <div className="text-base font-data font-bold text-white">${pick.entry?.toFixed(2)} → ${pick.target?.toFixed(2)}</div>
            </div>
            <div className="bg-black/40 border border-zinc-800 rounded p-2">
              <div className="text-muted-foreground">Stop · R/R</div>
              <div className="text-base font-data font-bold text-white">${pick.stop?.toFixed(2)} · {pick.rr ?? "—"}</div>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-muted-foreground mb-1">Pillars</div>
            {["fundamental","technical","unusual_activity","catalyst"].map((d) => {
              const meta = PILLAR_META[d];
              const p = pick.pillars?.[d] || {};
              return (
                <div key={d} className="flex items-center gap-2 text-xs py-1">
                  <span className={`w-16 ${p.filled ? "text-white" : "text-zinc-600"}`}>{meta.label}</span>
                  <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                    <div className={`h-full ${meta.color}`} style={{ width: `${p.filled ? p.score : 0}%` }} />
                  </div>
                  <span className="w-8 text-right font-data">{p.filled ? p.score : "—"}</span>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function WatchlistTab() {
  const [allPicks, setAllPicks] = useState([]);
  const [counts, setCounts] = useState({});
  const [edgeMinusHidden, setEdgeMinusHidden] = useState(0);
  const [showEdgeMinus, setShowEdgeMinus] = useState(false);
  const [edgePlusOnly, setEdgePlusOnly] = useState(false);
  const [learning, setLearning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState("ALL");
  const [dismissed, setDismissed] = useState(() => readLocalSet(DISMISSED_KEY));
  const [interested, setInterested] = useState(() => readLocalSet(INTERESTED_KEY));
  const [detailPick, setDetailPick] = useState(null);

  const fetchPicks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "25" });
      if (showEdgeMinus) params.set("include_edge_minus", "true");
      if (edgePlusOnly) params.set("min_hit_rate", "60");
      const [r, lr] = await Promise.all([
        api.get(`/sohel-agent/watchlist?${params.toString()}`),
        api.get("/sohel-agent/learning").catch(() => ({ data: null })),
      ]);
      setAllPicks(r.data?.picks || []);
      setCounts(r.data?.counts || {});
      setEdgeMinusHidden(r.data?.edge_minus_hidden || 0);
      setLearning(lr.data);
    } catch {
      toast.error("Failed to load watchlist");
    } finally {
      setLoading(false);
    }
  }, [showEdgeMinus, edgePlusOnly]);

  useEffect(() => { fetchPicks(); }, [fetchPicks]);

  const visiblePicks = useMemo(() => {
    return allPicks.filter((p) => {
      if (dismissed.has(p.symbol)) return false;
      if (tierFilter !== "ALL" && p.tier !== tierFilter) return false;
      return true;
    });
  }, [allPicks, dismissed, tierFilter]);

  const topPick = visiblePicks[0];
  const nextPick = visiblePicks[1];

  const handleSwipe = (direction) => {
    if (!topPick) return;
    if (direction === "right") {
      const ni = new Set(interested); ni.add(topPick.symbol); setInterested(ni); writeLocalSet(INTERESTED_KEY, ni);
      toast.success(`★ ${topPick.symbol} saved to interested`, {
        action: { label: "Paper trade", onClick: () => { window.location.href = "/paper-portfolio"; } }
      });
    } else {
      const nd = new Set(dismissed); nd.add(topPick.symbol); setDismissed(nd); writeLocalSet(DISMISSED_KEY, nd);
      toast(`${topPick.symbol} dismissed`);
    }
  };

  const resetDismissed = () => {
    setDismissed(new Set()); writeLocalSet(DISMISSED_KEY, new Set());
    toast.success("Dismissed picks cleared");
  };

  return (
    <div className="space-y-4">
      <Card className="card-dark border-border">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/10">
              <Flame className="w-5 h-5 text-emerald-300" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-white">Today's Watchlist</h2>
              <p className="text-xs text-muted-foreground">
                Only picks where the agents found <span className="text-white">real signal</span> — noise is filtered out.
                {visiblePicks.length} pick{visiblePicks.length !== 1 ? "s" : ""} ·{" "}
                <span className="text-emerald-300">{counts.action || 0} strong</span> ·{" "}
                <span className="text-amber-300">{counts.watch || 0} solid</span> ·{" "}
                <span className="text-sky-300">{counts.early || 0} early</span>
              </p>
            </div>
            <Button data-testid="watchlist-refresh" variant="ghost" size="sm" onClick={fetchPicks} disabled={loading} className="h-9">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>

          {/* 🧠 Self-teaching learning banner */}
          {learning && learning.total_outcomes > 0 && (
            <div className="bg-gradient-to-r from-fuchsia-500/10 to-emerald-500/10 border border-fuchsia-400/20 rounded-lg p-2.5 flex items-center justify-between gap-2 text-[11px]">
              <div className="flex items-center gap-2">
                <Brain className="w-4 h-4 text-fuchsia-300" />
                <span className="text-zinc-200">
                  <b>{learning.total_outcomes}</b> closed picks learned ·{" "}
                  <span className={learning.overall_hit_rate >= 50 ? "text-emerald-300" : "text-rose-300"}>
                    {learning.overall_hit_rate}% win rate
                  </span>
                </span>
              </div>
              {learning.top_sources?.length > 0 && (
                <span className="text-muted-foreground truncate">
                  Best source: <b className="text-emerald-300">{learning.top_sources[0].name}</b> ({learning.top_sources[0].hit_rate}%)
                </span>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {[
              { k: "ALL",    label: "All" },
              { k: "ACTION", label: "Strong" },
              { k: "WATCH",  label: "Solid" },
              { k: "EARLY",  label: "Early" },
            ].map((t) => (
              <Button
                key={t.k}
                data-testid={`watchlist-filter-${t.k.toLowerCase()}`}
                size="sm"
                variant={tierFilter === t.k ? "default" : "outline"}
                onClick={() => setTierFilter(t.k)}
                className="h-8 text-xs"
              >{t.label}</Button>
            ))}
            <Button
              data-testid="watchlist-edge-plus"
              size="sm"
              variant={edgePlusOnly ? "default" : "outline"}
              onClick={() => setEdgePlusOnly((v) => !v)}
              className={`h-8 text-xs ${edgePlusOnly ? "bg-emerald-600 hover:bg-emerald-500 text-white" : ""}`}
              title="High-% Performance Scanner: only show picks where the historical backtest hit ≥ 60%"
            >
              🎯 EDGE+ only
            </Button>
            {dismissed.size > 0 && (
              <Button data-testid="watchlist-reset-dismissed" size="sm" variant="ghost" onClick={resetDismissed} className="h-8 text-xs ml-auto">
                Restore {dismissed.size} dismissed
              </Button>
            )}
          </div>

          {/* EDGE− toggle row */}
          {(edgeMinusHidden > 0 || showEdgeMinus) && (
            <div className="flex items-center justify-between gap-2 pt-2 border-t border-zinc-800">
              <div className="text-[11px] text-muted-foreground">
                {showEdgeMinus
                  ? "Showing all picks — including those where the historical analog has been a loser."
                  : `${edgeMinusHidden} pick${edgeMinusHidden !== 1 ? "s" : ""} hidden because the backtest fingerprint failed historically.`}
              </div>
              <Button
                data-testid="watchlist-toggle-edge-minus"
                size="sm"
                variant="ghost"
                onClick={() => setShowEdgeMinus((v) => !v)}
                className="h-7 text-xs"
              >
                {showEdgeMinus ? "Hide EDGE−" : "Show EDGE−"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Card stack */}
      {loading ? (
        <Card className="card-dark border-border h-[520px] flex items-center justify-center">
          <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
        </Card>
      ) : !topPick ? (
        <Card className="card-dark border-border h-[300px]">
          <CardContent className="p-8 text-center space-y-3 flex flex-col items-center justify-center h-full">
            <CheckCircle2 className="w-10 h-10 text-emerald-400" />
            <p className="text-base text-zinc-300">No high-conviction picks right now.</p>
            <p className="text-xs text-muted-foreground max-w-sm">
              The agents only surface stocks where multiple pillars align AND the historical-analog
              backtest shows real edge. When nothing crosses the bar, this is empty by design.
              {edgeMinusHidden > 0 && (
                <> ({edgeMinusHidden} pick{edgeMinusHidden !== 1 ? "s" : ""} hidden as EDGE−.)</>
              )}
            </p>
            <div className="flex gap-2">
              {edgeMinusHidden > 0 && !showEdgeMinus && (
                <Button onClick={() => setShowEdgeMinus(true)} variant="outline" size="sm">
                  Show {edgeMinusHidden} EDGE− pick{edgeMinusHidden !== 1 ? "s" : ""}
                </Button>
              )}
              {dismissed.size > 0 && (
                <Button onClick={resetDismissed} variant="outline" size="sm">Restore dismissed picks</Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="relative h-[560px] max-w-md mx-auto" onClick={() => topPick && setDetailPick(topPick)}>
            <AnimatePresence>
              {nextPick && <SwipeCard key={nextPick.symbol + "-back"} pick={nextPick} onSwipe={() => {}} top={false} />}
              {topPick && <SwipeCard key={topPick.symbol + "-top"} pick={topPick} onSwipe={handleSwipe} top onTap={() => setDetailPick(topPick)} />}
            </AnimatePresence>
          </div>

          {/* Action buttons (also useful on desktop where no swipe) */}
          <div className="flex items-center justify-center gap-4">
            <Button
              data-testid="watchlist-btn-dismiss"
              onClick={() => handleSwipe("left")}
              className="h-14 w-14 rounded-full bg-rose-500/20 hover:bg-rose-500/30 border border-rose-400/40 text-rose-300"
              variant="outline"
            >
              <X className="w-6 h-6" />
            </Button>
            <Button
              data-testid="watchlist-btn-info"
              onClick={(e) => { e.stopPropagation(); setDetailPick(topPick); }}
              className="h-12 w-12 rounded-full bg-sky-500/20 hover:bg-sky-500/30 border border-sky-400/40 text-sky-300"
              variant="outline"
            >
              <Info className="w-5 h-5" />
            </Button>
            <Button
              data-testid="watchlist-btn-like"
              onClick={() => handleSwipe("right")}
              className="h-14 w-14 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/40 text-emerald-300"
              variant="outline"
            >
              <Heart className="w-6 h-6" />
            </Button>
          </div>

          {/* Interested list */}
          {interested.size > 0 && (
            <Card className="card-dark border-border">
              <CardContent className="p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
                  ★ Your interested list ({interested.size})
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {[...interested].map((sym) => (
                    <Badge key={sym} className="bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 text-xs font-data">
                      {sym}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <DetailDialog pick={detailPick} onClose={() => setDetailPick(null)} />
    </div>
  );
}
